import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

export interface PopoverOptions {
	spacing?: number;
	placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
	offset?: { x: number; y: number };
	closeOnScroll?: boolean;
	closeOnResize?: boolean;
	closeOnBreakpoint?: boolean;
	usePortal?: boolean;
}

export interface PopoverPosition {
	left: number;
	top: number;
	placement: 'top' | 'bottom' | 'left' | 'right';
	caretLeft: number;
	caretTop?: number;
	caretVisible: boolean;
}

export function usePopover(options: PopoverOptions = {}) {
	const {
		spacing = 8,
		placement = 'auto',
		offset = { x: 0, y: 0 },
		closeOnScroll = true,
		closeOnResize = true,
		closeOnBreakpoint = true,
		usePortal = true,
	} = options;

	// Memoize offset values to prevent unnecessary recalculations
	const offsetX = offset.x || 0;
	const offsetY = offset.y || 0;

	const [isOpen, setIsOpen] = useState(false);
	const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
	const [popoverPosition, setPopoverPosition] =
		useState<PopoverPosition | null>(null);
	const [measuredDimensions, setMeasuredDimensions] = useState<{
		width: number;
		height: number;
	} | null>(null);
	const triggerRef = useRef<HTMLElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	const calculatePosition = useCallback(
		(rect: DOMRect, popoverWidth = 280, popoverHeight = 300) => {
			const viewportHeight = window.innerHeight;
			const viewportWidth = window.innerWidth;
			const padding = 12;

			// Calculate available space on all sides
			const spaceBelow = viewportHeight - rect.bottom - padding;
			const spaceAbove = rect.top - padding;
			const spaceRight = viewportWidth - rect.right - padding;
			const spaceLeft = rect.left - padding;

			// Check what fits
			const canFitBelow = spaceBelow >= popoverHeight;
			const canFitAbove = spaceAbove >= popoverHeight;
			const canFitRight =
				spaceRight >= popoverWidth &&
				viewportHeight - padding * 2 >= popoverHeight;
			const canFitLeft =
				spaceLeft >= popoverWidth &&
				viewportHeight - padding * 2 >= popoverHeight;

			// Determine placement
			let finalPlacement: 'top' | 'bottom' | 'left' | 'right';
			let caretVisible = true;

			if (placement === 'auto') {
				if (canFitBelow) {
					finalPlacement = 'bottom';
				} else if (canFitAbove) {
					finalPlacement = 'top';
				} else if (canFitRight) {
					finalPlacement = 'right';
				} else if (canFitLeft) {
					finalPlacement = 'left';
				} else {
					// Nothing fits perfectly - use bottom with clamping, hide caret
					finalPlacement = 'bottom';
					caretVisible = false;
				}
			} else if (placement === 'left' || placement === 'right') {
				finalPlacement = placement;
			} else {
				finalPlacement = placement;
			}

			// Calculate position based on placement
			let top: number;
			let left: number;
			let caretLeft = 0;
			let caretTop: number | undefined;

			if (finalPlacement === 'bottom') {
				top = rect.bottom + spacing;
				left = rect.left + offsetX;

				// Clamp vertically if needed
				if (top + popoverHeight > viewportHeight - padding) {
					top = viewportHeight - popoverHeight - padding;
					caretVisible = false;
				}

				caretLeft = Math.max(
					8,
					Math.min(rect.left - left + 16, popoverWidth - 24),
				);
			} else if (finalPlacement === 'top') {
				top = rect.top - popoverHeight - spacing;
				left = rect.left + offsetX;
				caretLeft = Math.max(
					8,
					Math.min(rect.left - left + 16, popoverWidth - 24),
				);
			} else if (finalPlacement === 'right') {
				left = rect.right + spacing;

				// Align top of popover with top of trigger, then clamp
				top = rect.top;

				// If it would go off the bottom, shift up
				if (top + popoverHeight > viewportHeight - padding) {
					top = viewportHeight - popoverHeight - padding;
				}

				// Ensure it doesn't go above viewport
				if (top < padding) {
					top = padding;
				}

				// Calculate caret position to point at trigger center
				caretTop = Math.max(
					8,
					Math.min(rect.top - top + rect.height / 2 - 8, popoverHeight - 24),
				);
			} else {
				// left - same logic
				left = rect.left - popoverWidth - spacing;

				top = rect.top;

				if (top + popoverHeight > viewportHeight - padding) {
					top = viewportHeight - popoverHeight - padding;
				}

				if (top < padding) {
					top = padding;
				}

				caretTop = Math.max(
					8,
					Math.min(rect.top - top + rect.height / 2 - 8, popoverHeight - 24),
				);
			}

			// Horizontal clamping for vertical placements
			if (finalPlacement === 'top' || finalPlacement === 'bottom') {
				if (left + popoverWidth > viewportWidth - padding) {
					left = Math.max(padding, rect.right - popoverWidth);
				}

				if (left < padding) {
					left = padding;
				}
			}

			return {
				left,
				top: top + offsetY,
				placement: finalPlacement,
				caretLeft,
				caretTop,
				caretVisible,
			};
		},
		[spacing, placement, offsetX, offsetY],
	);

	// Ref-based throttling to prevent infinite loops
	const throttleTimeoutRef = useRef<number | null>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) {
			return;
		}

		// Check if trigger is still in DOM
		if (!document.contains(triggerRef.current)) {
			setIsOpen(false);
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();

		// Check if trigger element is visible in viewport
		const isVisible =
			rect.width > 0 &&
			rect.height > 0 &&
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0;

		if (!isVisible) {
			setIsOpen(false);
			return;
		}

		setTriggerRect(rect);
	}, []);

	// Throttled version for resize events using ref
	const throttledUpdatePosition = useCallback(() => {
		if (!isOpen) {
			return;
		} // Only update when popover is actually open

		if (throttleTimeoutRef.current) {
			clearTimeout(throttleTimeoutRef.current);
		}

		throttleTimeoutRef.current = window.setTimeout(() => {
			updatePosition();
			throttleTimeoutRef.current = null;
		}, 16); // ~60fps
	}, [isOpen, updatePosition]);

	const open = useCallback(() => {
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();

			// Check if trigger is visible before opening
			const isVisible =
				rect.width > 0 &&
				rect.height > 0 &&
				rect.top < window.innerHeight &&
				rect.bottom > 0 &&
				rect.left < window.innerWidth &&
				rect.right > 0;

			if (isVisible) {
				setTriggerRect(rect);
				setIsOpen(true);
			}
		}
	}, []);

	const close = useCallback(() => {
		setIsOpen(false);
	}, []);

	const toggle = useCallback(() => {
		if (isOpen) {
			close();
		} else {
			open();
		}
	}, [isOpen, open, close]);

	// Update popover position when trigger rect or measured dimensions change.
	// useLayoutEffect (not useEffect) so the recomputed position flushes
	// synchronously before the browser paints — otherwise the panel can paint
	// one frame positioned from the assumed default dimensions before the
	// measured-dimensions recalculation lands.
	useLayoutEffect(() => {
		if (triggerRect != null) {
			const position = calculatePosition(
				triggerRect,
				measuredDimensions?.width ?? 280,
				measuredDimensions?.height ?? 300,
			);
			setPopoverPosition(position);
		}
	}, [triggerRect, calculatePosition, measuredDimensions]);

	// Measure popover content before paint so positioning (and its viewport
	// clamping) uses the real dimensions instead of the 280x300 defaults.
	// While unpositioned the panel renders invisible-but-laid-out (see
	// getPopoverStyle), so a synchronous measure on the first open succeeds.
	// The ResizeObserver re-measures whenever the panel's size changes while
	// open (async content loading, or swapping panels inside one session like
	// the saved-search dropdown ↔ manage-tabs view).
	useLayoutEffect(() => {
		if (isOpen && popoverRef.current != null) {
			const element = popoverRef.current;

			const measure = (): void => {
				const rect = element.getBoundingClientRect();

				if (rect.width > 0 && rect.height > 0) {
					// Preserve object identity when unchanged so ResizeObserver
					// callbacks can't cause render loops.
					setMeasuredDimensions((prev) =>
						prev != null &&
						prev.width === rect.width &&
						prev.height === rect.height
							? prev
							: { width: rect.width, height: rect.height },
					);
				}
			};

			measure();

			const observer = new ResizeObserver(measure);
			observer.observe(element);

			return (): void => {
				observer.disconnect();
			};
		} else if (!isOpen) {
			// Reset on close so the next open re-measures and repositions from
			// scratch instead of flashing this session's (now stale) position.
			setMeasuredDimensions(null);
			setPopoverPosition(null);
		}
		return undefined;
	}, [isOpen]);

	// Event listeners and monitoring
	useEffect(() => {
		if (isOpen) {
			const handleClickOutside = (event: MouseEvent) => {
				if (
					triggerRef.current &&
					!triggerRef.current.contains(event.target as Node)
				) {
					const popoverElement = event.target as Element;
					if (!popoverElement.closest('[data-popover-content]')) {
						setIsOpen(false);
					}
				}
			};

			document.addEventListener('mousedown', handleClickOutside);

			if (closeOnScroll) {
				window.addEventListener('scroll', throttledUpdatePosition, true);
			}

			if (closeOnResize) {
				window.addEventListener('resize', throttledUpdatePosition);
			}

			// Position monitoring for responsive changes
			const checkInterval = setInterval(() => {
				if (triggerRef.current) {
					if (!document.contains(triggerRef.current)) {
						setIsOpen(false);
						return;
					}

					const currentRect = triggerRef.current.getBoundingClientRect();

					// Check if trigger element is still visible
					const isVisible =
						currentRect.width > 0 &&
						currentRect.height > 0 &&
						currentRect.top < window.innerHeight &&
						currentRect.bottom > 0 &&
						currentRect.left < window.innerWidth &&
						currentRect.right > 0;

					if (!isVisible) {
						setIsOpen(false);
						return;
					}

					// Check for significant position changes
					if (triggerRect) {
						const positionChanged =
							Math.abs(currentRect.left - triggerRect.left) > 50 ||
							Math.abs(currentRect.top - triggerRect.top) > 50;

						if (positionChanged) {
							setTriggerRect(currentRect);
						}
					}
				}
			}, 100); // Reduced frequency to 100ms for better performance

			// Breakpoint monitoring
			let mediaQueries: MediaQueryList[] = [];
			if (closeOnBreakpoint) {
				const breakpoints = [
					'(max-width: 640px)',
					'(max-width: 768px)',
					'(max-width: 1024px)',
					'(max-width: 1280px)',
				];

				const handleBreakpointChange = () => {
					// Use a small delay to allow layout changes to complete
					setTimeout(updatePosition, 50);
				};

				mediaQueries = breakpoints.map((bp) => {
					const mq = window.matchMedia(bp);
					mq.addListener(handleBreakpointChange);
					return mq;
				});
			}

			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
				if (closeOnScroll) {
					window.removeEventListener('scroll', throttledUpdatePosition, true);
				}
				if (closeOnResize) {
					window.removeEventListener('resize', throttledUpdatePosition);
				}
				clearInterval(checkInterval);
				// Clear any pending throttled updates
				if (throttleTimeoutRef.current) {
					clearTimeout(throttleTimeoutRef.current);
					throttleTimeoutRef.current = null;
				}
				if (closeOnBreakpoint) {
					mediaQueries.forEach((mq) => {
						mq.removeListener(() => setTimeout(updatePosition, 10));
					});
				}
			};
		}
		return undefined;
	}, [
		isOpen,
		closeOnScroll,
		closeOnResize,
		closeOnBreakpoint,
		updatePosition,
		throttledUpdatePosition,
		triggerRect,
	]);

	// Cleanup on unmount
	useEffect(
		() => () => {
			setIsOpen(false);
		},
		[],
	);

	return {
		isOpen,
		open,
		close,
		toggle,
		triggerRef,
		popoverRef,
		popoverPosition,
		// Helper for getting popover style
		getPopoverStyle: () =>
			popoverPosition
				? usePortal
					? {
							position: 'fixed' as const,
							left: popoverPosition.left,
							top: popoverPosition.top,
							zIndex: 9999,
						}
					: {
							position: 'absolute' as const,
							left: 0,
							top: '100%',
							marginTop: spacing,
							zIndex: 50,
							minWidth: triggerRect?.width || 'auto',
						}
				: usePortal
					? {
							// Not positioned yet (first frame of an open): keep the panel
							// laid out but invisible so the measurement effect can read its
							// real dimensions before the first paint. `display: none` here
							// measures 0x0, which forced positioning onto assumed default
							// dimensions — the cause of the "first open renders half
							// off-screen" bug.
							position: 'fixed' as const,
							top: 0,
							left: 0,
							visibility: 'hidden' as const,
							zIndex: 9999,
						}
					: { display: 'none' as const },
		// Helper for caret positioning
		getCaretProps: (size = 8) =>
			popoverPosition
				? {
						left: usePortal ? popoverPosition.caretLeft + size : 16,
						top: popoverPosition.caretTop,
						placement: popoverPosition.placement,
						size,
						visible: popoverPosition.caretVisible,
					}
				: null,
	};
}
